import { Alert, Button, Card } from "react-bootstrap";
import { AUTH_MODES } from "@/api/mcp.js";

/** Un bloque de texto para copiar entero: una orden de consola, un JSON, un TOML. */
const Snippet = ({ children }) => (
  <div className="position-relative">
    {/* `text-wrap` de Bootstrap pone white-space: normal y se come los saltos
        de linea del bloque; pre-wrap los conserva y ademas parte las lineas
        largas, que en un movil son casi todas. */}
    <pre
      className="bg-body-secondary border rounded p-3 pe-5 small text-break mb-0"
      style={{ whiteSpace: "pre-wrap" }}
    >
      {children}
    </pre>
    <Button
      variant="outline-secondary"
      size="sm"
      className="position-absolute top-0 end-0 m-2"
      aria-label="Copiar"
      title="Copiar"
      onClick={() => navigator.clipboard?.writeText(children)}
    >
      <i className="bi bi-clipboard" />
    </Button>
  </div>
);

/** Un cliente y lo que hay que pegarle, con el dónde antes que el qué. */
const Cliente = ({ nombre, donde, codigo }) => (
  <div className="mb-3">
    <div className="fw-semibold">{nombre}</div>
    <div className="text-muted small mb-1">{donde}</div>
    <Snippet>{codigo}</Snippet>
  </div>
);

const conLlave = (url) => [
  {
    nombre: "Claude Code",
    donde: "Una orden en la terminal y listo.",
    codigo: `claude mcp add --transport http cfdi ${url} \\\n  --header "Authorization: Bearer TU_LLAVE"`,
  },
  {
    nombre: "Cursor",
    donde:
      "En ~/.cursor/mcp.json, o en .cursor/mcp.json si lo quieres sólo para un proyecto. Es el mismo formato que piden otros clientes de escritorio.",
    codigo: JSON.stringify(
      {
        mcpServers: {
          cfdi: { url, headers: { Authorization: "Bearer TU_LLAVE" } },
        },
      },
      null,
      2,
    ),
  },
  {
    nombre: "Codex",
    donde:
      "En ~/.codex/config.toml. La llave no va en el archivo: va en una variable de entorno (export CFDI_MCP_TOKEN=…), y Codex la manda como cabecera.",
    codigo: `[mcp_servers.cfdi]\nurl = "${url}"\nbearer_token_env_var = "CFDI_MCP_TOKEN"`,
  },
];

const conOauth = (url) => [
  {
    nombre: "Claude Code",
    donde:
      "Sin cabecera ninguna: al conectarse abre el navegador para que la persona entre con su cuenta.",
    codigo: `claude mcp add --transport http cfdi ${url}`,
  },
  {
    nombre: "Cursor",
    donde:
      "Igual, sin headers: Cursor sabe pedir el permiso por OAuth tal como lo describe el protocolo.",
    codigo: JSON.stringify({ mcpServers: { cfdi: { url } } }, null, 2),
  },
  {
    nombre: "Codex",
    donde:
      "Codex manda un token fijo, no abre el navegador: en esa variable va un token que te haya dado tu servidor de autorización.",
    codigo: `[mcp_servers.cfdi]\nurl = "${url}"\nbearer_token_env_var = "CFDI_MCP_TOKEN"`,
  },
];

/**
 * Cómo conectar un cliente, que es lo que nadie sabe de memoria y lo que hace
 * falta justo después de encender el MCP.
 *
 * Los pasos no son los mismos en los dos modos, así que se enseñan los del modo
 * elegido y sólo ésos: una página con las dos recetas a la vez obliga al
 * contador a decidir cuál le toca, y ya la decidió arriba.
 *
 * De cada cliente se dice primero dónde va la configuración —que es la parte
 * que se busca a tientas— y después qué se pega.
 *
 * La dirección sale de lo que contesta el servidor, no de una constante: en el
 * servidor de pruebas y en producción no es la misma, y una URL escrita a mano
 * aquí sería correcta en un sitio y mentira en el otro.
 */
const McpSetup = ({ configuration }) => {
  const usaLlave = configuration.authMode === AUTH_MODES.API_KEY;
  const url = configuration.endpoint;
  const clientes = usaLlave ? conLlave(url) : conOauth(url);

  return (
    <Card className="mb-3">
      <Card.Header>Cómo conectarlo</Card.Header>
      <Card.Body>
        {usaLlave ? (
          <>
            <ol className="ps-3">
              <li className="mb-3">
                Emite una llave abajo y cópiala en ese momento.{" "}
                <strong>No se vuelve a mostrar</strong>: si se pierde, se revoca
                y se emite otra.
              </li>
              <li className="mb-3">
                Pégala en tu cliente, donde <code>TU_LLAVE</code>:
                <div className="mt-2">
                  {clientes.map((cliente) => (
                    <Cliente key={cliente.nombre} {...cliente} />
                  ))}
                </div>
              </li>
              <li>
                Pregúntale algo del despacho —&quot;¿cuántas facturas están
                pendientes de atender?&quot;— para comprobar que entró.
              </li>
            </ol>
            <Alert variant="secondary" className="small mb-0">
              Si el cliente se queja: <strong>401</strong> es llave equivocada o
              revocada, y <strong>503</strong> es que el MCP está apagado
              arriba. No hay que reiniciar nada al cambiar la configuración; la
              siguiente consulta ya la lee.
            </Alert>
          </>
        ) : (
          <>
            <ol className="ps-3">
              <li className="mb-3">
                En tu servidor de autorización, da de alta este MCP como recurso
                con la audiencia <code>{url}</code>, y registra ahí el cliente
                que se va a conectar. Los accesos se reparten y se retiran desde
                allá, no desde esta pantalla.
              </li>
              <li className="mb-3">
                Copia arriba el emisor, esa misma audiencia y la URL del JWKS, y
                guarda.
              </li>
              <li className="mb-3">
                Configura el cliente. No necesita ninguna llave nuestra: lee{" "}
                <code>{configuration.metadataUrl}</code> y de ahí se entera de a
                qué servidor ir a pedir permiso.
                <div className="mt-2">
                  {clientes.map((cliente) => (
                    <Cliente key={cliente.nombre} {...cliente} />
                  ))}
                </div>
              </li>
              <li>Comprueba con una consulta cualquiera del despacho.</li>
            </ol>
            <Alert variant="warning" className="small mb-0">
              <strong>Lo que el token tiene que traer</strong> para que lo
              aceptemos: firma RS256, RS384 o RS512; <code>iss</code> igual al
              emisor configurado; <code>aud</code> igual a la audiencia;{" "}
              <code>exp</code> presente —un token sin caducidad se rechaza—; y
              un <code>email</code> (o <code>preferred_username</code>, o{" "}
              <code>sub</code>) que corresponda a un contador dado de alta en
              este sistema. Si la persona no existe aquí como contador, no entra
              aunque su token sea válido.
            </Alert>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default McpSetup;
